import {
	Box,
	Checkbox,
	CircularProgress,
	Collapse,
	Divider,
	SelectChangeEvent,
	Typography,
} from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import LabeledSelectField from '@src/components/CustomSelect/LabeledSelectField';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import { ModalCloseIcon } from '@src/components/Icons';
import ImageThumnail from '@src/components/ImagePreview/ImageThumnail';
import DragDrop from '@src/components/ImageUploader/DragDrop';
import { IsMobileWidth, IsTabletWidth } from '@src/components/MediaQueryUtils';
import PageHeader from '@src/components/PageHeader/PageHeader';
import { CustomAlertType, Image } from '@src/interfaces/type';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { OrderView } from '..';
import { FileError } from '@src/pages/Products';
import { cleanObject, generateUniqueID, validateFilePixelsAndSize } from '@src/helpers/Helpers';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { OrderProduct } from '../acceptDelivery';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	letter-spacing: 0.28px;
	width: 100%;
	height: auto;
	max-height: 500px;
	@media screen and (max-width: 600px) {
		height: 90vh;
		max-height: 90vh;
		overflow: hidden;
	}
	#mid {
		display: flex;
		flex-direction: column;
		gap: 14px;
		align-items: center;
		padding: 16px;
		width: 100%;
		overflow-y: auto;
		height: auto;
		flex-grow: 1;

		@media screen and (max-width: 600px) {
			/* height: calc(100vh - 166px);*/
			margin-bottom: 70px;
		}
		#product-details {
			display: flex;
			justify-content: space-between;
			align-items: center;
			color: var(--Gray-g-600, #7d7d7d);
			font-size: 13px;
			font-weight: 600;
			width: 100%;
			@media screen and (max-width: 600px) {
				flex-direction: column;
				align-items: flex-start;
				gap: 14px;
			}
		}
	}
`;

const AcceptedBox = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-radius: 6px;
	border: 1.5px solid var(--Gray-g-10, #f1f1f1);
	background: var(--Gray-g-0, #fdfdfd);
	width: 100%;
	padding-left: 6px;
	p {
		color: var(--Gray-g-700, #5c5c5c);
		font-family: Poppins;
		font-size: 14px;
		font-weight: 500;
		line-height: 23.4px;
	}
`;

const StyledBox = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	border-radius: 6px;
	border-left: 1.5px solid var(--Gray-g-10, #f1f1f1);
	border-right: 1.5px solid var(--Gray-g-10, #f1f1f1);
	border-bottom: 1.5px solid var(--Gray-g-10, #f1f1f1);
	background: var(--others-white, #fff);
	#heading {
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-radius: 6px;
		border: 1.5px solid var(--Gray-g-10, #f1f1f1);
		background: var(--Gray-g-0, #fdfdfd);
		width: 100%;
		padding-left: 6px;
		p {
			color: var(--Gray-g-700, #5c5c5c);
			font-family: Poppins;
			font-size: 14px;
			font-weight: 500;
			line-height: 23.4px;
		}
	}
	#fields {
		display: flex;
		flex-direction: column;
		gap: 14px;
		padding: 12px;
		#field-container {
			display: flex;
			align-items: center;
			gap: 15px;
			@media screen and (max-width: 768px) {
				flex-direction: column;
				align-items: flex-start;
				gap: 14px;
			}
		}
	}
`;

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	width: 100%;
	height: 68px;
	padding: 20px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	flex-shrink: 0;
	@media screen and (max-width: 600px) {
		z-index: 100;
		position: absolute;
		bottom: 0;
	}
`;

interface ProductQualityCheckProps {
	productDetails: OrderProduct;
	windowDimensions: any;
	handleClose: any;
	setView: (data: OrderView) => void;
	type: 'accept' | 'reject';
	setApiCall: any;
	setAlert: (data: CustomAlertType) => void;
	displayCustomError: any;
	setQualityCheckModal: any;
}

const ProductQualityCheck: React.FC<ProductQualityCheckProps> = ({
	productDetails,
	windowDimensions,
	handleClose,
	setView,
	type,
	setApiCall,
	setAlert,
	displayCustomError,
	setQualityCheckModal,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const [product, setProduct] = useState({
		quantity: '',
		issue: '',
		comment: '',
		resolution: '',
	});
	const [acceptedProduct, setAcceptedProduct] = useState({ feedback: '' });
	const options = [
		{ id: '1', value: 'Missing Product' },
		{ id: '2', value: 'Wrong Quantity' },
		{ id: '3', value: 'Defective' },
		{ id: '4', value: 'Other' },
	];
	//file-upload
	const [files, setFiles] = useState<File[]>([]);
	const [images, setImages] = useState<Image[]>([]);
	const [loading, setLoading] = useState(false);
	const isMobileWidth = IsMobileWidth();
	const [activeCheckbox, setActiveCheckbox] = useState<'accept' | 'reject'>(type);
	const isAccepted = activeCheckbox === 'accept';

	const resolutionOptions = [
		{
			id: '1',
			value: 'replacement',
		},
		{
			id: '2',
			value: 'credit note',
		},
		{
			id: '3',
			value: 'custom request',
		},
	];

	const handleCheckboxChange = (type: 'accept' | 'reject') => {
		setActiveCheckbox(type);
	};

	const pixels = {
		height: 1000,
		width: 1000,
	};
	const handleChange = () => {};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();
		const filesData = event?.target?.files;
		let imageCount = images?.length + (filesData?.length || 0);
		let validFiles = [];
		let imagesData = [...images];
		let errorMessages: FileError[] = [];
		let files = [];

		if (imageCount <= 1) {
			if (filesData) {
				for (let i = 0; i < filesData.length; i++) {
					const file = filesData[i];
					validFiles.push(file);
					const fileURL = URL.createObjectURL(file);
					imagesData.push({ id: generateUniqueID(), url: fileURL, hover: false });
					files?.push(file);
				}
			}
			setFiles([...files]);
			setImages(imagesData);
		} else {
			displayCustomError('Maximum of 2 images allowed');
		}
	};

	const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const filesData = event.dataTransfer?.files;
		let imageCount = images?.length + (filesData?.length || 0);
		let validFiles = [];
		let imagesData = [...images];
		let errorMessages: FileError[] = [];
		let files = [];

		if (imageCount <= 1) {
			if (filesData) {
				for (let i = 0; i < filesData.length; i++) {
					const file = filesData[i];
					validFiles.push(file);
					const fileURL = URL.createObjectURL(file);
					imagesData.push({ id: generateUniqueID(), url: fileURL, hover: false });
					files?.push(file);
				}
			}
			setFiles([...files]);
			setImages(imagesData);
		} else {
			displayCustomError('Maximum of 2 images allowed');
		}
	};

	const handleArrowBack = () => {
		setView('orderDetails');
	};

	const disableStyles = {};
	const fieldsStyle1 = { width: '84vw', height: '36px' };
	const fieldsStyle2 = { width: '198px', height: '36px' };
	const fieldStyles = { width: '412px', height: '36px' };

	const disableBtn = () => {
		if (
			activeCheckbox !== 'accept' &&
			(!product?.quantity || !product?.issue || !product?.resolution || loading)
		) {
			return true;
		}
	};

	const uploadImages = () => {
		const uploadPromises = [];

		for (let i = 0; i < files?.length; i++) {
			const file = files[i];
			const formData = new FormData();
			formData.append('file', file);

			const uploadPromise: any = new Promise((resolve, reject) => {
				apiService?.uploadProductImage(
					formData,
					(response: AxiosResponse) => {
						resolve(response?.data?.data?.url);
					},
					(error: AxiosError) => {
						displayCustomError('Something went wrong');
						reject(error);
					}
				);
			});

			uploadPromises.push(uploadPromise);
		}

		return Promise.all(uploadPromises);
	};

	const updateProduct = async () => {
		setLoading(true);
		let data;
		if (isAccepted) {
			data = cleanObject({
				status: 'ACCEPTED',
				comments: acceptedProduct?.feedback,
			});
		} else {
			try {
				const isRejected = Number(productDetails?.quantity) === Number(product?.quantity);
				let imagesUrl;
				if (images?.length) {
					imagesUrl = await uploadImages();
				}

				data = cleanObject({
					status: isRejected ? 'REJECTED' : 'PARTIALLY_ACCEPTED',
					quantity: product?.quantity,
					reason: options?.find((option: any) => option?.id === product?.issue)?.value,
					comments: product?.comment,
					image: imagesUrl?.[0],
					resolution: resolutionOptions?.find((option: any) => option?.id === product?.resolution)
						?.value,
				});
			} catch (err) {
				setLoading(false);
				return;
			}
		}
		apiService.updateOrderProduct(
			data,
			(res: AxiosResponse) => {
				setApiCall(true);
				setAlert({ message: 'Product Updated Sussfully', severity: 'success' });
				setLoading(false);
				setQualityCheckModal(false);
			},
			(error: AxiosError) => {
				displayCustomError('Something went wrong!');
				setLoading(false);
			},
			productDetails?.order_product_id
		);
	};

	const handleQuantity = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const inputValue = e?.target?.value as string;
		const onlyDigits = inputValue.replace(/\D/g, '');

		if (
			onlyDigits === '' ||
			(Number(onlyDigits) <= Number(productDetails?.quantity) && Number(onlyDigits) !== 0)
		) {
			setProduct({ ...product, quantity: onlyDigits });
		}
	};

	return (
		<StyledContainer>
			{isMobileWidth ? (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-start',
						alignItems: 'flex-start',
						padding: '20px 10px 10px 10px',
						width: '100%',
						flexShrink: 0,
					}}
				>
					<p
						style={{
							color: '#5C5C5C',
							fontFamily: 'Poppins',
							fontSize: '16px',
							fontWeight: 400,
						}}
					>
						Product quality check
					</p>
				</Box>
			) : null}
			<Divider style={{ width: '100%' }} />
			<div id="mid">
				<div id="product-details">
					<p>{productDetails?.name}</p>
					<span>Qty: ({productDetails?.quantity})</span>
				</div>
				<StyledBox>
					<div id="heading">
						<p>Accepted</p>
						<Checkbox
							checked={activeCheckbox === 'accept'}
							onChange={() => handleCheckboxChange('accept')}
						/>
					</div>
					<Collapse in={activeCheckbox === 'accept'}>
						<div id="fields">
							<LabeledTextField
								label="Comment"
								value={acceptedProduct?.feedback}
								onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
									setAcceptedProduct({ ...acceptedProduct, feedback: e?.target?.value as string })
								}
								multiline
								rows={3}
								placeholder="write here"
								styles={{ width: windowDimensions?.width < 800 ? '100%' : '412px' }}
							/>
						</div>
					</Collapse>
				</StyledBox>

				<StyledBox>
					<div id="heading">
						<p>Rejected</p>
						<Checkbox
							checked={activeCheckbox === 'reject'}
							onChange={() => handleCheckboxChange('reject')}
						/>
					</div>
					<Collapse in={activeCheckbox === 'reject'}>
						<div id="fields">
							<div id="field-container">
								<LabeledTextField
									required
									label="Enter Rejected Quantity"
									value={product?.quantity}
									onChange={handleQuantity}
									placeholder="e.g 3 defective"
									styles={windowDimensions?.width < 800 ? fieldsStyle1 : fieldsStyle2}
								/>
								<LabeledSelectField
									required
									label="Select Issue"
									value={product?.issue}
									handleChange={(e: SelectChangeEvent<unknown>) => {
										setProduct({ ...product, issue: e?.target?.value as string });
									}}
									options={options}
									disablePortal={false}
									placeholder="e.g Missing Product"
									styles={windowDimensions?.width < 800 ? fieldsStyle1 : fieldsStyle2}
								/>
							</div>
							<LabeledTextField
								label="Comment"
								value={product?.comment}
								onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
									setProduct({ ...product, comment: e?.target?.value as string })
								}
								multiline
								rows={3}
								placeholder="write here"
								styles={{ width: windowDimensions?.width < 800 ? '84vw' : '412px' }}
							/>
							<LabeledSelectField
								required
								label="Resolution"
								value={product?.resolution}
								handleChange={(e: SelectChangeEvent<unknown>) => {
									setProduct({ ...product, resolution: e?.target?.value as string });
								}}
								options={resolutionOptions}
								disablePortal={false}
								placeholder="e.g replacement"
								styles={{ width: windowDimensions?.width < 800 ? '84vw' : '412px' }}
							/>
							{images?.length ? (
								<ImageThumnail
									images={images}
									setImages={setImages}
									setFiles={setFiles}
									style={{ width: windowDimensions?.width < 800 ? '84vw' : '412px' }}
									handleFileChange={handleFileChange}
									windowDimensions={windowDimensions}
									maxLength={1}
								/>
							) : (
								<DragDrop
									handleFileChange={handleFileChange}
									handleFileDrop={handleFileDrop}
									style={{ width: windowDimensions?.width < 800 ? '84vw' : '412px' }}
									text="Capture / select image to upload"
									info="Image in png or jpg format."
									multiple
								/>
							)}
						</div>
					</Collapse>
				</StyledBox>
			</div>
			<StyledBottomBtnContainer>
				<CustomButton
					title="Cancel"
					handleClick={() => setQualityCheckModal(false)}
					styles={{
						width: '132px',
						height: '36px',
						background: 'var(--btn-color-grey)',
						color: 'var(--default-text-color)',
						':hover': {
							background: 'var(--btn-hover-color) !important',
						},
					}}
				/>
				<CustomButton
					type="submit"
					title={
						loading ? (
							<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
						) : (
							'Submit'
						)
					}
					handleClick={updateProduct}
					disabled={disableBtn()}
					styles={{
						width: '132px',
						height: '36px',
					}}
				/>
			</StyledBottomBtnContainer>
		</StyledContainer>
	);
};

export default ProductQualityCheck;
